import { useMemo, useState } from 'react';
import clsx from 'clsx';
import SweeperWorkspace from '@/components/queue/SweeperWorkspace';
import MissedDepositsList from '@/components/queue/MissedDepositsList';
import { buildMissedDeposits } from '@/domain/missedDeposits';
import { ALL_STUDIOS } from '@/data/personas';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';

type SweeperTab = 'fill' | 'missed';

/**
 * Operations Lead sweeper — one screen (bd tv-ch5 + tv-d2f, board 08). Two
 * top-level tabs: "Fill this week's gaps" (artist-at-a-time gap ladder) and
 * "Missed deposits" (unpaid gap-fill links past their 24h lock, the morning
 * priority; 48h+ unclosed escalates to Ops). The old "Money & open business"
 * cohort list is retired — deposit-on-file and open business are rungs of the
 * fill ladder.
 */
export default function Sweeper() {
  const [tab, setTab] = useState<SweeperTab>('fill');

  const deposits = useBrainStore((s) => s.deposits);
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const missed = useMemo(
    () => buildMissedDeposits({ nowIso: clockIso, deposits, customers, bookings, studioIds: ALL_STUDIOS }),
    [clockIso, deposits, customers, bookings],
  );

  return (
    <section className="px-10 py-8" data-testid="sweeper-screen">
      <header className="max-w-6xl">
        <p className="label-small-caps text-gold-bright">Sweeper</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Fill this week&rsquo;s gaps</h1>
        <p className="font-serif text-cream-soft mt-1">
          This week · every studio · one artist at a time.
        </p>
      </header>

      <div className="mt-6 flex items-center gap-6 border-b border-gold-dim/25">
        <Stab label="Fill this week's gaps" active={tab === 'fill'} onClick={() => setTab('fill')} testid="sweeper-tab-fill" />
        <Stab
          label="Missed deposits"
          count={missed.length}
          active={tab === 'missed'}
          onClick={() => setTab('missed')}
          testid="sweeper-tab-missed"
        />
      </div>

      <div className="mt-6">
        {tab === 'fill' ? <SweeperWorkspace /> : <MissedDepositsList rows={missed} />}
      </div>
    </section>
  );
}

function Stab({
  label,
  count,
  active,
  onClick,
  testid,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'flex items-center gap-2 pb-2.5 -mb-px border-b-2 text-[13px] font-semibold transition',
        active ? 'border-gold-bright text-cream' : 'border-transparent text-cream-faint hover:text-cream-soft',
      )}
    >
      {label}
      {count !== undefined && count > 0 ? (
        <span className="rounded-full bg-ink-soft px-1.5 py-px font-mono text-[10px] tabular-nums text-cream-soft">
          {count}
        </span>
      ) : null}
    </button>
  );
}
